type RGIS = 'RGIS';
type USER_TOKEN = 'mut';
type CORAL_TOKEN = 'mct';
type VerifyIdTokenResult = {
    tokens: Record<RGIS | CORAL_TOKEN | USER_TOKEN, string | null | undefined>;
    emailHash?: string;
};
type EmailHash = {
    alg: string;
    digest: string;
};

type KnownUser = {
    userProfile: {
        email: string | null;
    };
};

export const verifyIdToken = async (idToken: string, siteKeyword: string): Promise<VerifyIdTokenResult> => {
    let res: Response;
    let data;
    try {
        res = await fetch('/.api/verify-id-token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ idToken, siteKeyword }),
        });
        data = await res.json();
    } catch (error) {
        throw error;
    }

    if (!res.ok) {
        throw new Error(`${data.message}/${res.status}`);
    }

    return {
        tokens: {
            // https://github.com/themaven-net/web-chilliwack/blob/46ac5f2b9b2ab0570446bf46cb8208f7ab78f95f/src/util/UserLoginStore.ts#L50
            RGIS: data?.tokens?.rgis ? JSON.stringify(data.tokens.rgis) : null,
            mct: data?.tokens?.coralToken?.token,
            mut: data?.tokens?.userToken?.token,
        },
        emailHash: data?.userResponse?.emailHashes?.find((item: EmailHash) => item.alg === 'sha256')?.digest,
    };
};

export const sendEmailLink = async (
    baseUrl: string,
    email: string,
    siteId: string,
    targetUrl: string
): Promise<void> => {
    let res: Response;
    try {
        res = await fetch(`${baseUrl}/api/public/identity/send-email-link`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, siteId, targetUrl }),
        });
    } catch (error) {
        throw error;
    }

    if (!res.ok) {
        throw new Error(`${(await res.json()).message}/${res.status}`);
    }
};

export const resolveEmailLink = async (sid: string, oobCode: string): Promise<string> => {
    const res = await fetch('/.api/resolve-email-link', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ oobCode, sid }),
    });
    const data = await res.json();

    if (!res.ok) {
        throw new Error(`${data.message ?? data.error}/${res.status}`);
    }

    if (!data.email) {
        throw new Error('Email is missing');
    }

    return data.email;
};

// Regex from https://emailregex.com/
// See https://github.com/themaven-net/taiga/blob/master/server/src/util/__tests__/emailHelper.test.ts for use cases
const validEmailRegex =
    // eslint-disable-next-line max-len
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
export const isValidEmail = (email?: string): boolean => {
    if (!email) return false;
    return Boolean(email.match(validEmailRegex));
};

export function getKnownUser(baseUrl: string, rgisCookieValue?: string | null): Promise<KnownUser> {
    if (!rgisCookieValue || !baseUrl) {
        return Promise.resolve({
            userProfile: {
                email: null,
            },
        });
    }
    return fetch(`${baseUrl}/api/user/profile`, {
        headers: {
            'Content-Type': 'application/json',
            // For some reason the returned cookie has quotes.
            'X-RGIS': rgisCookieValue.replace(/^"/, '').replace(/"$/, ''),
        },
    }).then((resp) => {
        if (resp.status !== 200) {
            return {
                userProfile: {
                    email: null,
                },
            };
        }
        return resp.json();
    });
}
